import { useState } from 'react';
import { useKeyboard } from './useKeyboard';
import type { CatalogResponse } from '@dastarhan/contracts';
import { BASE } from '../api';
import { tt } from '../i18n';

/** Стоп-лист без офиса: закончился казы — кассир стопит в два тапа.
 *  Снимается само в полночь; вернуть можно тем же тапом. */
export function StopDialog({ token, catalog, onClose, onChanged }: {
  token: string;
  catalog: CatalogResponse;
  onClose: () => void;
  onChanged: () => void;
}) {
  // Остаток порций: «пусто» значит «закончилось совсем», и это
  // самый частый случай — поэтому кнопка стоит первой
  const [asking, setAsking] = useState<string | null>(null);
  // Пресетов не хватает: у кальянной бывает и 7 чашек, и 24 бутылки.
  // Даём вписать своё число — с цифровой клавиатурой планшета
  const [rest, setRest] = useState('');
  const [q, setQ] = useState('');
  const kb = useKeyboard(q, setQ, 'text');
  const kbRest = useKeyboard(rest, setRest, 'number');
  const [busy, setBusy] = useState<string | null>(null);
  const stops = catalog.stopList ?? {};
  const since = catalog.stopSince ?? {};
  // возраст стопа — та же планка, что у бота: от двух дней
  const ageOf = (id: string): string => {
    const at = since[id];
    if (!at) return '';
    const d = Math.floor((Date.now() - new Date(at).getTime()) / 86400_000);
    return d >= 2 ? ` [${d} дн.]` : '';
  };
  const rows = catalog.products
    .filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()))
    // стопнутые вверх, среди них — давние первыми: это то,
    // что требует решения, а не просто списка
    .sort((x, y) => {
      const sx = x.id in stops ? 1 : 0;
      const sy = y.id in stops ? 1 : 0;
      if (sx !== sy) return sy - sx;
      const ax = since[x.id] ? new Date(since[x.id]!).getTime() : Infinity;
      const ay = since[y.id] ? new Date(since[y.id]!).getTime() : Infinity;
      return ax - ay;
    })
    .slice(0, 40);
  // в большом меню список урезан — честно сказать об этом
  const totalMatched = catalog.products.filter((p) =>
    !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())).length;
  /**
   * Применить стоп. remaining=null значит «закончилось совсем»,
   * число — сколько порций ещё можно пробить.
   */
  const apply = async (productId: string, remaining: number | null) => {
    setBusy(productId);
    try {
      await fetch(`${BASE}/menu/stop-my`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId, off: false, untilEndOfDay: true, remaining }),
      });
      onChanged();
    } catch {
      /*
       * Стоп-лист живёт на сервере — это его сила (видят все кассы и
       * офис) и его честная цена: без связи менять его нельзя.
       * Молчание тут хуже отказа — кассир должен знать, что стоп
       * НЕ встал, и крикнуть бару по-старинке
       */
      alert('Не получилось: стоп-лист меняется на сервере. Проверьте связь и повторите.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Нажали на блюдо. Снять со стопа — сразу; поставить — спросив
   * остаток: цифры на планшете набирают своей клавиатурой
   */
  const toggle = (productId: string, stopped: boolean) => {
    if (stopped) { void applyOff(productId); return; }
    setRest('');
    setAsking(productId);
  };

  /** Снятие со стопа: вопросов не требует, блюдо снова в продаже. */
  const applyOff = async (productId: string) => {
    setBusy(productId);
    try {
      await fetch(`${BASE}/menu/stop-my`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId, off: true, untilEndOfDay: true, remaining: null }),
      });
      onChanged();
    } finally { setBusy(null); }
  };

  return (
    <>
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Стоп-лист">
      <div className="dialog">
        <span className="eyebrow">Меню</span>
        <h1>{tt('stopTitle')}</h1>
        {Object.keys(stops).length > 0 && (() => {
          const all = Object.values(stops);
          const low = all.filter((x) => typeof x === 'number' && x > 0).length;
          return (
            <p className="hint">
              На стопе сейчас: {all.length - low}
              {low > 0 ? ` · на исходе: ${low}` : ''}
            </p>
          );
        })()}
        <input {...kb.props} className="field" style={{ width: '100%' }}
          placeholder={tt('stopFind')} value={q} onChange={(e) => setQ(e.target.value)} />
        {kb.node}
        {totalMatched > rows.length && (
          <p className="hint">Показаны первые {rows.length} из {totalMatched} — уточните поиск</p>
        )}
        <div className="stop-list">
          {rows.map((p) => {
            const stopped = p.id in stops;
            return (
              <button key={p.id} className={`stop-row ${stopped ? 'off' : ''}`}
                disabled={busy === p.id}
                onClick={() => void toggle(p.id, stopped)}>
                <span>{p.name}
                  {typeof stops[p.id] === 'number' && stops[p.id]! > 0 && (
                    <small className="order-age"> — осталось {stops[p.id]}</small>
                  )}
                  <small className="order-age">{ageOf(p.id)}</small>
                </span>
                <b>{stopped ? tt('stopReturn') : tt('stopStop')}</b>
              </button>
            );
          })}
        </div>
        <p className="hint" style={{ margin: 0 }}>{tt('stopHint')}</p>
        <div className="dialog-actions">
          <button className="btn ghost" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>

      {asking && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true"
          aria-label="Остаток порций" onClick={() => setAsking(null)}>
          <div className="dialog prompt-card" onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow">Стоп-лист</span>
            <h1>Сколько порций осталось?</h1>
            <p className="hint">
              Касса не даст пробить больше. Если закончилось совсем —
              нажмите «Закончилось».
            </p>
            <div className="stop-rest">
              {[1, 2, 3, 5, 10].map((n) => (
                <button key={n} className="btn" onClick={() => { void apply(asking, n); setAsking(null); }}>
                  {n}
                </button>
              ))}
            </div>
            <label className="stop-rest-manual">
              <span>Или впишите своё число</span>
              <input {...kbRest.props} className="field" inputMode="numeric"
                placeholder="напр. 7" value={rest}
                onChange={(e) => setRest(e.target.value.replace(/\D/g, '').slice(0, 4))} />
            </label>
            {kbRest.node}
            <div className="dialog-actions">
              <button className="btn ghost" onClick={() => setAsking(null)}>Отмена</button>
              {rest !== '' && Number(rest) > 0 && (
                <button className="btn primary"
                  onClick={() => { void apply(asking, Number(rest)); setAsking(null); }}>
                  Осталось {rest}
                </button>
              )}
              <button className="btn accent" onClick={() => { void apply(asking, null); setAsking(null); }}>
                Закончилось
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}